import { Box, ColorPicker, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import TextInput from 'modules/core/components/Inputs/Text'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import ActionBox from 'modules/shift_management/components/ShiftManagement/ActionBox'
import ShiftAccessSelectors from 'modules/shift_management/components/ShiftManagement/ShiftAccessSelectors'
import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import { buildSMColorNames, hexCodeFromName } from 'modules/shift_management/lib/colors'
import type DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import { trackAddDefaultShiftStart } from 'modules/shift_management/lib/trackEvents'

export interface Props {
  form: DefaultShiftForm
  defaultTitle: string
  extraHoursValue: boolean
}

const SaveShiftToggle = ({ form, defaultTitle, extraHoursValue }: Props) => {
  const [{ hasFeatureFlag, canUseSaveShiftToggle }] = useEnabledFeatures()
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()

  const hasColors = hasFeatureFlag('shift_management_colours_for_shifts')
  const [hasSeenSaveShiftCallout, setHasSeenSaveShiftCallout] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHideSaveShiftToggle,
    false
  )

  useUnsafeEffect(() => {
    if (!form.get('title').value && form.isSavedFromCustom) {
      form.setValues({ title: defaultTitle })
    }
  }, [form, defaultTitle, form.isSavedFromCustom])

  useEffect(() => form.setValues({ extra_hours: extraHoursValue }), [extraHoursValue])

  if (!canUseSaveShiftToggle) {
    return (
      <ActionBox
        title={i18n.t('shift_management.actions_sidepanel.toggle.upgrade_box.title')}
        description={i18n.t('shift_management.actions_sidepanel.toggle.upgrade_box.description')}
        upgradeFeature={Features.shift_management_save_shift_toggle}
      />
    )
  }

  return (
    <Box flexDirection="row" gap="s16" width="full">
      <Box alignItems="center" paddingY="s16">
        <TooltipIcon.Medium
          icon="save"
          label={i18n.t('shift_management.actions_sidepanel.toggle.title')}
          color="grey700"
        />
      </Box>
      <Box
        gap="s16"
        padding="s16"
        borderRadius={{ all: 'abs016' }}
        border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
        width="full"
      >
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Text.Normal weight="semibold">
            {i18n.t('shift_management.actions_sidepanel.toggle.title')}
          </Text.Normal>
          <TogglerCheckbox
            value={form.isSavedFromCustom}
            onChange={() => {
              form.setValues({ saved_from_custom: !form.isSavedFromCustom })
              trackAddDefaultShiftStart({
                daysPreselected: hasSelection,
                view,
                from: 'save_shift_toggle',
              })
            }}
          />
        </Box>

        {!hasSeenSaveShiftCallout && (
          <Callout
            description={i18n.t('shift_management.actions_sidepanel.toggle.description')}
            type="info"
            onRemove={() => setHasSeenSaveShiftCallout(true)}
          />
        )}

        {form.isSavedFromCustom && (
          <>
            <TextInput
              label={i18n.t('shift_management.default_shift.form_labels.title')}
              field={form.get('title')}
              mandatory
              onChange={() => form.get('title').setErrors([])}
            />

            {hasColors && (
              <Box gap="s4" width="full">
                <Text.Normal size="200" weight="medium" color="grey1000">
                  {i18n.t('shift_management.add_shift.color_label')}
                </Text.Normal>
                <Box
                  paddingX="s6"
                  paddingY="s8"
                  borderRadius={{ all: 'abs100' }}
                  border={{ all: { width: 's2', style: 'solid', color: 'grey400' } }}
                  alignItems="center"
                >
                  <ColorPicker
                    value={form.get('color_name').value}
                    colors={buildSMColorNames()}
                    onChange={(value) => {
                      form.setValues({ color_name: value, color: hexCodeFromName(value) })
                    }}
                    defaultColor="olive1000"
                  />
                </Box>
              </Box>
            )}

            <Box gap="s8">
              <Text.Normal size="200" weight="medium" color="grey1000">
                {i18n.t('shift_management.saved_shifts.form_access.title')}
              </Text.Normal>
              <ShiftAccessSelectors form={form} newVersion />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default observer(SaveShiftToggle)
